import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Loader2, Search, StickyNote, Users } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { dictionary } from "../../../utils/dictionary";
import { handleApi } from "../../../utils/handleApi";
import {
  listContacts,
  type Contact,
} from "../../../services/whatsapp/whatsappService";

const formatDate = (value: string | null | undefined): string => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return value.slice(0, 10);
  }
};

const ContactsListPanel = () => {
  const { language } = useAuth();
  const t = (key: string, fallback: string) => dictionary[key]?.[language] ?? fallback;

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setLoading(true);
    const { res, error } = await handleApi(listContacts(undefined, 500), false);
    if (res) setContacts(res);
    else if (error) {
      toast.error(t("ws-contacts-load-error", "No se pudieron cargar los contactos"));
    }
    setLoading(false);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((c) => {
      return (
        (c.name || "").toLowerCase().includes(q) ||
        (c.alias || "").toLowerCase().includes(q) ||
        (c.email || "").toLowerCase().includes(q) ||
        c.phone.includes(q.replace(/^\+/, ""))
      );
    });
  }, [contacts, search]);

  return (
    <div className="max-w-7xl mx-auto p-8 font-['Plus_Jakarta_Sans']">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t("ws-contacts-title", "Lista de contactos")}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {t("ws-contacts-desc", "Todos los contactos de WhatsApp que han interactuado con tus embudos.")}
          </p>
        </div>
        <div className="inline-flex items-center gap-2 rounded-full bg-[#F0F2F5] text-[#667781] border border-gray-200 px-3 py-1.5 text-xs font-medium">
          <Users className="w-3.5 h-3.5" />
          <span className="font-semibold">{contacts.length}</span>
          <span className="opacity-75">
            {dictionary["chat-contact-counter-label"]?.[language] ?? "contactos"}
          </span>
        </div>
      </div>

      <div className="mb-4 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("ws-contacts-search-placeholder", "Buscar por nombre, teléfono o email...")}
          className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent bg-white"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-sm text-gray-400 py-16 border border-dashed border-gray-200 rounded-2xl bg-white">
          {search.trim()
            ? t("ws-contacts-empty-search", "No hay contactos que coincidan con tu búsqueda.")
            : t("ws-contacts-empty", "Aún no hay contactos.")}
        </div>
      ) : (
        <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="text-left font-semibold px-4 py-3">
                    {t("ws-contacts-col-name", "Nombre")}
                  </th>
                  <th className="text-left font-semibold px-4 py-3">
                    {t("ws-contacts-col-phone", "Teléfono")}
                  </th>
                  <th className="text-left font-semibold px-4 py-3">
                    {t("ws-contacts-col-email", "Email")}
                  </th>
                  <th className="text-left font-semibold px-4 py-3">
                    {t("ws-contacts-col-alias", "Alias")}
                  </th>
                  <th className="text-left font-semibold px-4 py-3">
                    {t("ws-contacts-col-tags", "Etiquetas")}
                  </th>
                  <th className="text-left font-semibold px-4 py-3">
                    {t("ws-contacts-col-notes", "Notas")}
                  </th>
                  <th className="text-left font-semibold px-4 py-3 whitespace-nowrap">
                    {t("ws-contacts-col-updated", "Última actualización")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr
                    key={c.id}
                    className="border-t border-gray-100 hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-3 text-gray-900 font-medium">
                      {c.name || <span className="text-gray-400 italic">—</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">+{c.phone}</td>
                    <td className="px-4 py-3 text-gray-700 break-all">
                      {c.email || <span className="text-gray-400 italic">—</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {c.alias || <span className="text-gray-400 italic">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {c.tags && c.tags.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {c.tags.map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-[#595EEE]/10 text-[#595EEE] border border-[#595EEE]/20"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400 italic">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      <span className="inline-flex items-center gap-1 text-xs">
                        <StickyNote className="w-3 h-3 text-gray-400" />
                        {(c.notes || []).length}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                      {formatDate(c.updated_at || c.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactsListPanel;
